import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import TaskService from 'src/services/tasks/taskService';
import { MeasureUnit, Region, Task, TaskGroupDTO, TaskGroupDTOPayload } from 'src/services/tasks/types';
import { DEFAULT_TABLE_PAGE_SIZE } from 'src/constants/Pagination';
import { isNotEmpty } from 'src/utils/helpers';

// **** Constants **** //

const initialState: TaskConfigPageState = {
  // Tasks
  isFetchTasksPending: false,
  taskTableData: [],
  taskTableSettings: {
    currentPage: 1,
    totalPages: 0,
    perPage: DEFAULT_TABLE_PAGE_SIZE,
  },
  taskSearchFieldValue: '',
  isTaskModalOpen: false,
  selectedTaskTableRow: null,
  // TaskGroups
  isFetchTaskGroupsPending: false,
  taskGroupTableData: [],
  taskGroupSearchFieldValue: '',
  isTaskGroupModalOpen: false,
  taskGroupTableSettings: {
    currentPage: 1,
    totalPages: 0,
    perPage: DEFAULT_TABLE_PAGE_SIZE,
  },
  selectedTaskGroupTableRow: null,
  // Shared
  unitsOfMeasure: [],
  regions: [],
};

// **** Types **** //

interface TableSettings {
  currentPage: number;
  totalPages: number;
  perPage: number;
}

export interface TaskTableDataRow {
  id: number;
  label: string;
  regions: string[];
  isMeasuredBy: string;
  countries: (string | undefined)[];
  identifyBodyLocation: boolean;
  description: string;
}

export interface TaskGroupTableDataRow {
  id: number;
  label: string;
  region: string;
  country: string;
  identifyBodyLocation: boolean;
  isMeasuredBy: string;
  tasks: TaskTableDataRow[];
}

interface TaskConfigPageState {
  // For the Tasks tab
  isFetchTasksPending: boolean;
  taskTableData: TaskTableDataRow[];
  taskTableSettings: TableSettings;
  taskSearchFieldValue: string;
  isTaskModalOpen: boolean;
  selectedTaskTableRow: TaskTableDataRow | null;
  // For the Task Groups tab
  isFetchTaskGroupsPending: boolean;
  taskGroupTableData: TaskGroupTableDataRow[];
  taskGroupSearchFieldValue: string;
  isTaskGroupModalOpen: boolean;
  taskGroupTableSettings: TableSettings;
  selectedTaskGroupTableRow: TaskGroupTableDataRow | null;
  // Shared
  unitsOfMeasure: MeasureUnit[];
  regions: Region[];
}

// **** Functions **** //

/**
 * Get TaskGroup Table Data row
 */
export const getBlankTaskGroupTableDataRow = (): TaskGroupTableDataRow => ({
  id: 0,
  label: '',
  region: '',
  country: '',
  identifyBodyLocation: false,
  isMeasuredBy: '',
  tasks: [],
});

/**
 * Convert task object to a table row
 */
const taskToTableRow = (task: Task): TaskTableDataRow => {
  const tableRow: TaskTableDataRow = {
    label: task.label,
    description: task.description ?? '',
    regions: task.regions?.map((r: Region) => r.name),
    countries: task.regions?.map((r: Region) => r.country?.name),
    isMeasuredBy: task.measureUnit?.name,
    identifyBodyLocation: task.identifyBodyLocation ? true : false,
  } as TaskTableDataRow;
  if (task.id !== undefined) {
    tableRow.id = task.id;
  }
  return tableRow;
};

/**
 * Convert a table row to a task object
 */
const tableRowToTask = (tableRow: TaskTableDataRow, regions: Region[], units: MeasureUnit[]): Task => {
  const task: Task = {
    label: tableRow.label,
    description: tableRow.description,
    identifyBodyLocation: tableRow.identifyBodyLocation,
    regions: tableRow.regions.map((rName: string) => regions.filter((r: Region) => rName === r.name)[0]),
    measureUnit: units.filter((unit: MeasureUnit) => tableRow.isMeasuredBy === unit.name)[0],
    // These fields need to be passed because backend requires it currently
    // will be removed later when backend fixed
    createdAt: '2022-02-25T23:06:47.000+00:00',
    updatedAt: new Date().toISOString(),
    id: tableRow.id !== undefined ? tableRow.id : '',
  } as Task;
  return task;
};

/**
 * Convert TaskGroupTableRow object to TaskGroup object.
 */
const formDataToTaskGroupDTOPayload = (
  formData: TaskGroupTableDataRow,
  regions: Region[],
  units: MeasureUnit[],
): TaskGroupDTOPayload => {
  const { id } = formData;
  return {
    ...(isNotEmpty(id) ? { id } : {}),
    label: formData.label,
    region: regions.filter((r) => formData.region === r.name)[0],
    tasks: formData.tasks.map((t) => tableRowToTask(t, regions, units)),
  };
};

/**
 * Convert TaskGroup object toTaskGroupTableRow object.
 */
const taskGroupToTableRow = (taskGroup: TaskGroupDTO): TaskGroupTableDataRow => {
  const taskTableRow: TaskGroupTableDataRow = {
    id: taskGroup.id,
    label: taskGroup.label,
    isMeasuredBy: '',
    identifyBodyLocation: false,
    region: taskGroup.region.name,
    country: taskGroup.region.country?.name ?? '',
    tasks: taskGroup.tasks?.map((t) => taskToTableRow(t)),
  };
  if (!!taskGroup.tasks[0]) {
    taskTableRow.isMeasuredBy = taskGroup.tasks[0].measureUnit.name;
    taskTableRow.identifyBodyLocation = taskGroup.tasks.some((t) => t.identifyBodyLocation);
  }
  return taskTableRow;
};

/**
 * Track which tasks already belong to a region.
 */
export const setupRegionTaskMap = (taskGroupTableData: TaskGroupTableDataRow[]): Map<string, Set<number>> => {
  const map = new Map<string, Set<number>>();
  taskGroupTableData.forEach((row) => {
    const set = map.get(row.region) ?? new Set();
    row.tasks.forEach((task) => {
      set.add(task.id);
    });
    map.set(row.region, set);
  });
  return map;
};

// **** Setup **** //

/**
 * Fetch units of measure
 */
export const fetchMeasureUnits = createAsyncThunk('tasks/fetchMeasureUnits', () => {
  return TaskService.fetchMeasureUnits();
});

/**
 * Fetch regions
 */
export const fetchRegions = createAsyncThunk('tasks/fetchRegions', () => {
  return TaskService.fetchRegions();
});

/**
 * Fetch all tasks
 */
export const fetchTasks = createAsyncThunk('tasks/fetchTasks', () => {
  return TaskService.fetchTasks();
});

/**
 * Add or Edit a Task record
 */
export const addOrEditTask = createAsyncThunk('tasks/addOrUpdateTask', async (task: TaskTableDataRow, { getState }) => {
  const taskConfigPageState: TaskConfigPageState = (getState() as any).taskConfigPage;
  const payload = tableRowToTask(task, taskConfigPageState.regions, taskConfigPageState.unitsOfMeasure);
  const respData = await TaskService.createOrUpdateTask(payload);
  if (respData) {
    payload.id = respData.id;
    return taskToTableRow(payload);
  }
});

/**
 * Fetch all task groups
 */
export const fetchTaskGroups = createAsyncThunk('tasks/fetchTaskGroups', () => {
  return TaskService.fetchTaskGroups();
});

/**
 * Add or Edit a TaskGroup record
 */
export const addOrEditTaskGroup = createAsyncThunk(
  'taskGroups/addOrUpdateTaskGroup',
  async (taskGroupFormData: TaskGroupTableDataRow, { getState, rejectWithValue }) => {
    const taskConfigPageState: TaskConfigPageState = (getState() as any).taskConfigPage;
    const { regions, unitsOfMeasure } = taskConfigPageState;
    const payload = formDataToTaskGroupDTOPayload(taskGroupFormData, regions, unitsOfMeasure);
    const respData = await TaskService.addOrUpdateTaskGroup(payload);
    if (respData) {
      return taskGroupToTableRow({ ...payload, id: respData.id });
    }
    return rejectWithValue('payload falsey');
  },
);

// ** Setup Redux Slice ** //
const { reducer, actions } = createSlice({
  name: 'taskConfigPageSlice',
  initialState,
  reducers: {
    setTaskSearchFieldValue: (state, action: PayloadAction<string>) => {
      state.taskSearchFieldValue = action.payload;
    },
    setIsTaskModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isTaskModalOpen = action.payload;
    },
    setSelectedTaskTableRow: (state, action: PayloadAction<TaskTableDataRow | null>) => {
      state.selectedTaskTableRow = action.payload;
    },
    setTaskTableSettings: (state, action: PayloadAction<Partial<TableSettings>>) => {
      state.taskTableSettings = {
        ...state.taskTableSettings,
        ...action.payload,
      };
    },
    setTaskGroupSearchFieldValue: (state, action: PayloadAction<string>) => {
      state.taskGroupSearchFieldValue = action.payload;
    },
    setIsTaskGroupModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isTaskGroupModalOpen = action.payload;
    },
    setSelectedTaskGroupTableRow: (state, action: PayloadAction<TaskGroupTableDataRow | null>) => {
      state.selectedTaskGroupTableRow = action.payload;
    },
    setTaskGroupTableSettings: (state, action: PayloadAction<Partial<TableSettings>>) => {
      state.taskTableSettings = {
        ...state.taskTableSettings,
        ...action.payload,
      };
    },
    /** @testonly */
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchTasks.pending, (state) => {
      state.isFetchTasksPending = true;
    });
    builder.addCase(fetchTasks.fulfilled, (state, { payload }) => {
      state.taskTableData = payload.map((task: any) => taskToTableRow(task));
      state.isFetchTasksPending = false;
    });
    builder.addCase(fetchTasks.rejected, (state) => {
      state.isFetchTasksPending = false;
    });
    builder.addCase(fetchMeasureUnits.fulfilled, (state, { payload }) => {
      state.unitsOfMeasure = payload;
    });
    builder.addCase(fetchRegions.fulfilled, (state, { payload }) => {
      state.regions = payload;
    });
    builder.addCase(addOrEditTask.fulfilled, (state, { payload }) => {
      if (payload) {
        state.taskSearchFieldValue = payload.label;
        const isAlreadyInTable = state.taskTableData.some((row) => {
          return row.id === payload.id;
        });
        // This will be the case when editing an existing site
        if (isAlreadyInTable) {
          state.taskTableData = state.taskTableData.map((row) => (row.id === payload.id ? payload : row));
          // This will be the case when adding a new site
        } else {
          state.taskTableData = [...state.taskTableData, payload];
        }
      }
    });
    builder.addCase(fetchTaskGroups.pending, (state) => {
      state.isFetchTaskGroupsPending = true;
    });
    builder.addCase(fetchTaskGroups.fulfilled, (state, { payload }) => {
      state.taskGroupTableData = payload.map((taskGroup) => taskGroupToTableRow(taskGroup));
      state.isFetchTaskGroupsPending = false;
    });
    builder.addCase(fetchTaskGroups.rejected, (state) => {
      state.isFetchTaskGroupsPending = false;
    });
    builder.addCase(addOrEditTaskGroup.fulfilled, (state, { payload }) => {
      if (payload) {
        state.taskGroupSearchFieldValue = payload.label;
        const isAlreadyInTable = state.taskGroupTableData.some((r) => r.id === payload.id);
        if (isAlreadyInTable) {
          state.taskGroupTableData = state.taskGroupTableData.map((r) => (r.id === payload.id ? payload : r));
        } else {
          state.taskGroupTableData = [...state.taskGroupTableData, payload];
        }
      }
    });
  },
});

// **** Export **** //

export const {
  setTaskSearchFieldValue,
  setIsTaskModalOpen,
  setSelectedTaskTableRow,
  setTaskTableSettings,
  setTaskGroupSearchFieldValue,
  setIsTaskGroupModalOpen,
  setSelectedTaskGroupTableRow,
  setTaskGroupTableSettings,
  resetState,
} = actions;

export default reducer;
